import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { FormField } from "components/form/FormField/FormField";
import { Button } from "components/ui/Button";
import { Dialog } from "components/ui/Dialog";
import { DialogFooter } from "components/ui/Dialog/DialogFooter";
import { Notice, NoticeText } from "components/ui/Notice";
import {
  FOETUS_OUTCOME_MAX_LENGTH,
  FOETUS_OUTCOME_SUGGESTIONS,
} from "features/pregnancy/constants/foetusOutcomes";
import { useEndPregnancyFoetus } from "features/pregnancy/hooks/useEndPregnancyFoetus";
import { describeFoetus } from "features/pregnancy/utils/endedFoetuses";
import { endFoetusFormSchema } from "features/pregnancy/utils/endFoetusFormSchema";
import { getServerErrorMessage } from "utils/showMutationError";

import style from "./EndFoetusDialog.module.css";

const OUTCOME_LIST_ID = "foetus-outcome-suggestions";

const EMPTY_FORM = { foetusId: "", outcome: "" };

/**
 * Recording that a foetus is no longer continuing (KIN-2066).
 *
 * The remedy the count-differs notice had no answer for: the maternity hospital reports fewer
 * foetuses than the record shows, and the likeliest reason is that one did not continue. So this
 * records an outcome against that baby — it is not a delete, and the wording never offers one.
 * The backend keeps the foetus and every observation ever recorded against it; what changes is
 * that new visits and the outbound message cover the remaining babies.
 *
 * Only live foetuses are offered, and the dialog is never the thing that decides whether the
 * action is available: a pregnancy's last foetus cannot be ended here — a pregnancy whose only
 * foetus did not continue is a completed pregnancy — so the callers hide the entry point and the
 * backend refuses it with a 409 besides, which is shown as it arrives.
 */
export const EndFoetusDialog = ({
  open,
  onClose,
  tenantVal,
  pregnancyId,
  liveFoetuses,
}) => {
  const form = useForm({
    resolver: zodResolver(endFoetusFormSchema),
    defaultValues: EMPTY_FORM,
  });

  const endFoetus = useEndPregnancyFoetus({
    tenantVal,
    pregnancyId,
    onSuccess: onClose,
  });

  /*
   * A foetus picked and an outcome typed then abandoned must not be waiting in the dialog the next
   * time it opens — they would be read as a decision about whatever pregnancy is on screen then.
   * The last failure is cleared with them, so a settled 409 does not sit over a fresh attempt.
   */
  useEffect(() => {
    if (open) {
      form.reset(EMPTY_FORM);
      endFoetus.reset();
    }
    // The mutation and the form are stable for the dialog's life; this is about the open edge only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selectedFoetusId = form.watch("foetusId");
  const outcome = form.watch("outcome");
  const selectedFoetus = liveFoetuses?.find(
    (foetus) => String(foetus.id) === selectedFoetusId
  );
  const trimmedOutcome = outcome?.trim() ?? "";

  const submit = form.handleSubmit((values) =>
    endFoetus.mutate({
      foetusId: Number(values.foetusId),
      outcome: values.outcome.trim(),
    })
  );

  return (
    <Dialog
      open={open}
      title="Record a foetus as not continuing"
      onClose={onClose}
      width={520}
    >
      <FormProvider {...form}>
        <form className={style.body} onSubmit={submit} noValidate>
          <p className={style.explanation}>
            This records what happened to one of the babies in this pregnancy.
            Nothing is deleted: the foetus and every observation already recorded
            against it are kept, and earlier visits still show them. Later visits
            and MN-CMS messages cover the remaining babies.
          </p>

          <FormField name="foetusId">
            <label className={style.label} htmlFor="end-foetus-which">
              Which foetus
            </label>
            <select
              id="end-foetus-which"
              className="form-control"
              {...form.register("foetusId")}
            >
              <option value="">Select a foetus</option>
              {liveFoetuses?.map((foetus) => (
                <option key={foetus.id} value={String(foetus.id)}>
                  {describeFoetus(foetus)}
                </option>
              ))}
            </select>
          </FormField>

          <FormField name="outcome">
            <label className={style.label} htmlFor="end-foetus-outcome">
              Outcome
            </label>
            <input
              id="end-foetus-outcome"
              className="form-control"
              list={OUTCOME_LIST_ID}
              placeholder="Fetal demise"
              autoComplete="off"
              maxLength={FOETUS_OUTCOME_MAX_LENGTH}
              {...form.register("outcome")}
            />
            {/* Suggestions, not a closed set — anything remains typeable. */}
            <datalist id={OUTCOME_LIST_ID}>
              {FOETUS_OUTCOME_SUGGESTIONS.map((suggestion) => (
                <option key={suggestion} value={suggestion} />
              ))}
            </datalist>
            <p className={style.hint}>
              Choose one of the suggestions or write your own.
            </p>
          </FormField>

          {/* Said back in the GP's own terms before anything is sent, naming the baby and the
              outcome together — and saying what is kept, because "not continuing" is the point at
              which a GP wonders what happens to the findings already recorded. Plain text, never
              markup: the outcome is free text typed by a user (KIN-2043). */}
          {!!selectedFoetus && (
            <p className={style.confirmation} data-testid="end-foetus-confirmation">
              {describeFoetus(selectedFoetus)} will be marked as not continuing
              {trimmedOutcome ? ` (${trimmedOutcome})` : ""}. Its recorded
              observations are kept.
            </p>
          )}

          {/* The backend's own words. Both 409s are expected answers — already ended, or the only
              remaining foetus — and both tell the GP what to do instead. */}
          {!!endFoetus.isError && (
            <Notice tone="blocking" testId="end-foetus-error">
              <NoticeText>
                {getServerErrorMessage(
                  endFoetus.error,
                  "This foetus could not be recorded as not continuing."
                )}
              </NoticeText>
            </Notice>
          )}

          <DialogFooter className={style.footer}>
            <Button variant="outline" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={endFoetus.isPending}>
              {endFoetus.isPending ? "Recording…" : "Record as not continuing"}
            </Button>
          </DialogFooter>
        </form>
      </FormProvider>
    </Dialog>
  );
};
